import { endOfMonth, format } from 'date-fns';
import { FC, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { buttonStyle } from 'components/buttons/buttonStyle';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import Section from 'components/section-menu/Section';
import SectionLayout from 'components/section-menu/SectionLayout';
import { Button } from 'components/ui/button';
import { useLazyGetOpenInvoicesQuery } from 'store/api/openInvoices';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { DEFAULT_DATE_FORMAT } from 'utils/parseDate';
import { generateOpenInvoicesExcel } from './openInvoicesExcel';

interface OpenInvoicesFormValues {
    asOfDate: string;
    includeWrittenOff: boolean;
}

const OpenInvoicesForm: FC = () => {
    // Computed on mount (not at module import) so the default reflects when the page is opened.
    const defaultValues = useMemo<OpenInvoicesFormValues>(
        () => ({ asOfDate: format(endOfMonth(new Date()), DEFAULT_DATE_FORMAT), includeWrittenOff: false }),
        []
    );
    const methods = useForm<OpenInvoicesFormValues>({ defaultValues, mode: 'onChange' });
    const { control, handleSubmit } = methods;

    const [fetchOpenInvoices, { isFetching }] = useLazyGetOpenInvoicesQuery();
    const [isExporting, setIsExporting] = useState(false);

    const onSubmit = useCallback(
        async (formData: OpenInvoicesFormValues) => {
            setIsExporting(true);
            try {
                const rows = await fetchOpenInvoices({ asOfDate: formData.asOfDate }).unwrap();
                await generateOpenInvoicesExcel(rows, formData.asOfDate, formData.includeWrittenOff);
            } catch {
                errorAlert(t('open-invoices.generate-error'));
            } finally {
                setIsExporting(false);
            }
        },
        [fetchOpenInvoices]
    );

    const actions = useMemo(
        () => [
            <Button
                key="open-invoices-export"
                {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                disabled={isFetching || isExporting}
                onClick={() => {
                    void handleSubmit(onSubmit)();
                }}
            >
                <FormattedMessage id="open-invoices.export" />
            </Button>,
        ],
        [handleSubmit, isFetching, isExporting, onSubmit]
    );

    return (
        <FormProvider {...methods}>
            <FormWrapper>
                <ActionHeader title={t('menu.open-invoices')} actions={actions} />
                <SectionLayout>
                    <Section title="open-invoices.section-title">
                        <div className="w-56">
                            <DateInput
                                control={control}
                                name="asOfDate"
                                label="open-invoices.as-of-date"
                                required
                                fullWidth
                            />
                        </div>
                        <CheckboxInput
                            control={control}
                            name="includeWrittenOff"
                            label="open-invoices.include-written-off"
                        />
                    </Section>
                </SectionLayout>
            </FormWrapper>
        </FormProvider>
    );
};

export default OpenInvoicesForm;
